import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { AssistantService } from '@core/services/assistant/assistant.service';

import {
  ModalComponent,
  ModalConfig,
  ModalSize
} from '@shared/components/modal/modal.component';

import { AssistantReportService } from './assistant-report.service';

@Component({
  selector: 'app-assistant-file-preview',
  imports: [ModalComponent, CdkTrapFocus, TranslatePipe],
  templateUrl: './assistant-file-preview.component.html',
  styles: ``
})
export class AssistantFilePreviewComponent {
  protected readonly assistant = inject(AssistantService);
  private readonly reports = inject(AssistantReportService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((e) => e.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );
  protected readonly ModalSize = ModalSize;

  // The report runs only Galia's own fixed script and loads nothing from the network: the report sanitizer
  // removes every script and on* attribute from the body, and the document's CSP admits only the template
  // script by hash. allow-scripts without allow-same-origin keeps it in an opaque origin, away from Galia.
  // This page's own CSP would also apply to the frame (see REPORT_SCRIPT_HASH). Angular's sanitizer is
  // bypassed because it would strip the template's <style>, CSP <meta> and script.
  protected readonly documentHtml = signal<SafeHtml | ''>('');
  private requested = 0;

  constructor() {
    effect(() => {
      this.currentLang();
      const file = this.assistant.previewFile();
      const request = ++this.requested;
      this.documentHtml.set('');
      if (!file) return;
      untracked(() => this.reports.document(file)).then(
        (html) => {
          if (request === this.requested) {
            this.documentHtml.set(this.sanitizer.bypassSecurityTrustHtml(html));
          }
        },
        // The frame was cleared above, so a document that cannot be built leaves it blank.
        () => undefined
      );
    });
  }

  protected readonly config = computed<ModalConfig>(() => {
    this.currentLang();
    const file = this.assistant.previewFile();
    return {
      icon: file?.format === 'markdown' ? 'fileMd' : 'fileHtml',
      dismissible: true,
      closeOnBackdropClick: true,
      actions: [
        {
          label: this.translate.instant('assistant.files.download'),
          icon: 'download',
          color: 'primary',
          callback: () => {
            if (file) this.reports.download(file);
          }
        }
      ]
    };
  });
}
